(() => {
  const MOBILE_QUERY = '(max-width: 749px)';

  function initFloatingMedia(floatEl) {
    if (!floatEl || floatEl.dataset.mobileFloatReady === 'true') return;
    floatEl.dataset.mobileFloatReady = 'true';

    const section = floatEl.closest('[id^="MainProduct-"]');
    const gallery = document.getElementById(floatEl.dataset.galleryId);
    const productInfo = document.getElementById(floatEl.dataset.productInfoId);
    const stopAt = floatEl.dataset.stopAtId ? document.getElementById(floatEl.dataset.stopAtId) : null;
    const floatingImage = floatEl.querySelector('img');
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const fadeDelay = Math.max(0, Number.parseInt(floatEl.dataset.fadeDelay || '3000', 10));

    if (!section || !gallery || !productInfo || !floatingImage) return;

    let ticking = false;
    let fadeTimer = null;
    let wasVisible = false;
    let lastImageUrl = '';

    const getConfiguredSize = () => {
      const requested = Number.parseInt(floatEl.dataset.floatSize || '120', 10) || 120;
      const viewportLimit = Math.max(90, window.innerWidth - 24);
      return Math.min(Math.max(requested, 90), viewportLimit);
    };

    const applyConfiguredSize = () => {
      const size = getConfiguredSize();
      floatEl.style.setProperty('--mobile-float-size', `${size}px`);
      // Inline dimensions make the theme-editor range reliable even if another
      // theme rule has a more specific width/height selector.
      floatEl.style.width = `${size}px`;
      floatEl.style.height = `${size}px`;
      floatingImage.setAttribute('width', String(size));
      floatingImage.setAttribute('height', String(size));
      return size;
    };

    const clearFadeTimer = () => {
      if (fadeTimer !== null) {
        window.clearTimeout(fadeTimer);
        fadeTimer = null;
      }
    };

    const scheduleFade = () => {
      clearFadeTimer();
      floatEl.classList.remove('is-faded');

      if (!floatEl.classList.contains('is-visible') || fadeDelay <= 0) return;

      fadeTimer = window.setTimeout(() => {
        if (floatEl.classList.contains('is-visible')) {
          floatEl.classList.add('is-faded');
        }
      }, fadeDelay);
    };

    const getActiveImage = () => (
      gallery.querySelector('.product__media-item.is-active:not(.hidden) img') ||
      gallery.querySelector('.product__media-item:not(.hidden) img') ||
      gallery.querySelector('img')
    );

    const syncImage = () => {
      const source = getActiveImage();
      if (!source) return false;

      const sourceUrl = source.currentSrc || source.getAttribute('src') || '';
      if (!sourceUrl || sourceUrl === lastImageUrl) return false;

      lastImageUrl = sourceUrl;
      floatingImage.src = sourceUrl;
      floatingImage.removeAttribute('srcset');
      return true;
    };

    const getHeaderBottom = () => {
      const candidates = [
        document.querySelector('sticky-header'),
        document.querySelector('.section-header'),
        document.querySelector('header.header')
      ].filter(Boolean);

      let bottom = 0;
      candidates.forEach((el) => {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        if (
          (style.position === 'sticky' || style.position === 'fixed') &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight
        ) {
          bottom = Math.max(bottom, rect.bottom);
        }
      });
      return bottom;
    };

    const getFallbackStopElement = () => (
      productInfo.querySelector('.product-form__quantity') ||
      document.querySelector('#az_reviews') ||
      document.querySelector('.areviews_main_header_container')?.closest('.shopify-section') ||
      document.querySelector('[id*="Areviews"]') ||
      document.querySelector('.jdgm-review-widget') ||
      document.querySelector('#shopify-product-reviews') ||
      document.querySelector('[data-reviews-widget]')
    );

    const hideFloat = () => {
      floatEl.classList.remove('is-visible', 'is-faded');
      clearFadeTimer();
      wasVisible = false;
    };

    const updateVisibility = () => {
      ticking = false;

      if (!mediaQuery.matches) {
        hideFloat();
        return;
      }

      const floatSize = applyConfiguredSize();
      const headerBottom = getHeaderBottom();
      const top = Math.max(12, Math.round(headerBottom + 10));
      section.style.setProperty('--mobile-product-float-top', `${top}px`);

      const galleryRect = gallery.getBoundingClientRect();
      const infoRect = productInfo.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const stopElement = stopAt || getFallbackStopElement();
      const stopRect = stopElement ? stopElement.getBoundingClientRect() : null;

      const hasPassedGallery = galleryRect.bottom <= top + 18;
      const stillInsideProduct =
        infoRect.bottom > top + floatSize * 0.45 &&
        sectionRect.bottom > top + floatSize * 0.45;

      // Hide just before the quantity control reaches the floating image area.
      // Once the customer reaches quantity, the floating image stays hidden.
      const beforeStop = !stopRect || stopRect.top > top + floatSize + 12;
      const shouldShow = hasPassedGallery && stillInsideProduct && beforeStop;

      if (!shouldShow) {
        hideFloat();
        return;
      }

      floatEl.classList.add('is-visible');
      const imageChanged = syncImage();

      if (!wasVisible || imageChanged) {
        scheduleFade();
      }
      wasVisible = true;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    };

    const refreshImageAfterVariantChange = () => {
      [60, 180, 420].forEach((delay) => {
        window.setTimeout(() => {
          if (syncImage()) scheduleFade();
          requestUpdate();
        }, delay);
      });
    };

    const galleryObserver = new MutationObserver(() => {
      if (syncImage()) scheduleFade();
      requestUpdate();
    });

    galleryObserver.observe(gallery, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'src', 'srcset']
    });

    document.addEventListener('change', (event) => {
      if (event.target.closest(`#${CSS.escape(productInfo.id)}`)) {
        refreshImageAfterVariantChange();
      }
    });

    document.addEventListener('variant:change', refreshImageAfterVariantChange);
    document.addEventListener('variant-change', refreshImageAfterVariantChange);

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', requestUpdate);

    applyConfiguredSize();
    syncImage();
    updateVisibility();
  }

  const initAll = () => {
    document.querySelectorAll('[data-mobile-product-media-float]').forEach(initFloatingMedia);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
